import api from '../lib/api';

export interface BookingReportFilters {
  hotel_id?: number;
  city?: string;
  state?: string;
  country?: string;
  checkin_date_from?: string;
  checkin_date_to?: string;
  booking_date_from?: string;
  booking_date_to?: string;
  booking_status?: string;
  payment_status?: string;
  page?: number;
  limit?: number;
}

export interface InventoryReportFilters {
  hotel_id?: number;
  room_type_id?: number;
  date_from?: string;
  date_to?: string;
  page?: number;
  limit?: number;
}

export interface HotelPerformanceFilters {
  hotel_id?: number;
  city?: string;
  booking_channel?: string;
  date_from?: string;
  date_to?: string;
  group_by?: 'weekly' | 'monthly' | 'yearly';
}

export interface BookingReportItem {
  id: number;
  booking_id: string;
  booking_reference: string;
  client_reference?: string;
  hotel_name: string;
  city: string;
  country: string;
  room_type_name: string;
  plan_name: string;
  meal_plan: string;
  guest_first_name: string;
  guest_last_name: string;
  guest_email: string;
  guest_phone?: string;
  guest_nationality?: string;
  checkin_date: string;
  checkout_date: string;
  nights_count: number;
  adults_count: number;
  children_count: number;
  rooms_count: number;
  base_amount: number;
  markup_amount: number;
  service_charge_amount: number;
  total_amount: number;
  currency: string;
  booking_status: string;
  payment_status: string;
  booking_source: string;
  booking_channel: string;
  booking_datetime: string;
  confirmation_datetime?: string;
  cancellation_datetime?: string;
  special_requests?: string;
  internal_remarks?: string;
}

export interface InventoryReportItem {
  hotel_id: number;
  hotel_name: string;
  city: string;
  country: string;
  room_type_id: number;
  room_type_name: string;
  room_description?: string;
  room_plan_id: number;
  plan_name: string;
  meal_plan: string;
  inventory_date: string;
  total_rooms: number;
  rooms_booked: number;
  rooms_available: number;
  occupancy_percentage: number;
}

export interface BookingReportResponse {
  success: boolean;
  data: {
    bookings: BookingReportItem[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      total_pages: number;
    };
  };
  filters: BookingReportFilters;
  timestamp: string;
}

export interface InventoryReportResponse {
  success: boolean;
  data: {
    inventory: InventoryReportItem[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      total_pages: number;
    };
  };
  filters: InventoryReportFilters;
  timestamp: string;
}

export interface HotelPerformanceSummary {
  total_bookings: number;
  fulfilled_bookings: number;
  cancelled_bookings: number;
  no_show_bookings: number;
  total_revenue: number;
  room_nights: number;
  average_daily_rate: number;
  average_length_of_stay: number;
  cancellation_rate: number;
  no_show_rate: number;
  occupancy_rate: number;
  revenue_per_available_room: number;
  rooms_booked: number;
  rooms_available: number;
  rooms_capacity: number;
}

export interface HotelPerformanceTimeseriesEntry {
  period_key: string;
  period_label: string;
  period_start: string | null;
  period_end: string | null;
  total_bookings: number;
  fulfilled_bookings: number;
  total_revenue: number;
  room_nights: number;
  average_daily_rate: number;
  average_length_of_stay: number;
  rooms_booked: number;
  rooms_available: number;
  occupancy_rate: number;
  revenue_per_available_room: number;
}

export interface HotelPerformanceHotelRow {
  hotel_id: number;
  hotel_name: string;
  city: string | null;
  country: string | null;
  total_bookings: number;
  fulfilled_bookings: number;
  cancelled_bookings: number;
  no_show_bookings: number;
  total_revenue: number;
  room_nights: number;
  average_daily_rate: number;
  average_length_of_stay: number;
  occupancy_rate: number;
  revenue_per_available_room: number;
  rooms_booked: number;
  rooms_available: number;
  rooms_capacity: number;
}

export interface HotelPerformanceRoomTypeBreakdown {
  room_type_name: string;
  total_bookings: number;
  room_nights: number;
  total_revenue: number;
  average_daily_rate: number;
}

export interface HotelPerformanceWeekdayOccupancy {
  weekday_index: number;
  weekday_label: string;
  rooms_booked: number;
  rooms_capacity: number;
  occupancy_rate: number;
}

export interface HotelPerformanceLeadTimeBucket {
  bucket: string;
  total_bookings: number;
}

export interface HotelPerformanceData {
  group_by: 'weekly' | 'monthly' | 'yearly';
  summary: HotelPerformanceSummary;
  timeseries: HotelPerformanceTimeseriesEntry[];
  hotels: HotelPerformanceHotelRow[];
  breakdowns: {
    room_types: HotelPerformanceRoomTypeBreakdown[];
    occupancy_by_weekday: HotelPerformanceWeekdayOccupancy[];
    lead_time_distribution: HotelPerformanceLeadTimeBucket[];
  };
}

export interface HotelPerformanceResponse {
  success: boolean;
  data: HotelPerformanceData;
  filters: {
    hotel_id: number | null;
    city: string | null;
    booking_channel: string | null;
    date_from: string;
    date_to: string;
    group_by: 'weekly' | 'monthly' | 'yearly';
  };
  timestamp: string;
}

class ReportsService {
  async getBookingReport(filters: BookingReportFilters): Promise<BookingReportResponse> {
    try {
      const params = new URLSearchParams();
      
      if (filters.hotel_id) params.append('hotel_id', filters.hotel_id.toString());
      if (filters.city) params.append('city', filters.city);
      if (filters.state) params.append('state', filters.state);
      if (filters.country) params.append('country', filters.country);
      if (filters.checkin_date_from) params.append('checkin_date_from', filters.checkin_date_from);
      if (filters.checkin_date_to) params.append('checkin_date_to', filters.checkin_date_to);
      if (filters.booking_date_from) params.append('booking_date_from', filters.booking_date_from);
      if (filters.booking_date_to) params.append('booking_date_to', filters.booking_date_to);
      if (filters.booking_status) params.append('booking_status', filters.booking_status);
      if (filters.payment_status) params.append('payment_status', filters.payment_status);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());

      const response = await api.get(`/reports/booking-datewise?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching booking report:', error);
      throw error;
    }
  }

  async getInventoryReport(filters: InventoryReportFilters): Promise<InventoryReportResponse> {
    try {
      const params = new URLSearchParams();
      
      if (filters.hotel_id) params.append('hotel_id', filters.hotel_id.toString());
      if (filters.room_type_id) params.append('room_type_id', filters.room_type_id.toString());
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());

      const response = await api.get(`/reports/inventory?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching inventory report:', error);
      throw error;
    }
  }

  async getHotelPerformanceReport(filters: HotelPerformanceFilters): Promise<HotelPerformanceResponse> {
    try {
      const params = new URLSearchParams();

      if (filters.hotel_id) params.append('hotel_id', filters.hotel_id.toString());
      if (filters.city) params.append('city', filters.city);
      if (filters.booking_channel) params.append('booking_channel', filters.booking_channel);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.group_by) params.append('group_by', filters.group_by);

      const response = await api.get(`/reports/hotel-performance?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching hotel performance report:', error);
      throw error;
    }
  }
}

export const reportsService = new ReportsService();
